import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { userAPI } from '../../api/user';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, refreshUserInfo } = useAuth();
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await userAPI.getProfile();
      setProfile(data);
    } catch (error) {
      toast.error(t('profile.security.toasts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.newPassword) {
      errors.newPassword = t('profile.security.errors.passwordRequired');
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = t('profile.security.errors.passwordTooShort');
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = t('profile.security.errors.confirmPasswordRequired');
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = t('profile.security.errors.passwordsNotMatch');
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setUpdating(true);
    
    try {
      await userAPI.updateProfile({ password: passwordData.newPassword });
      toast.success(t('profile.security.toasts.success'));
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(t('profile.security.toasts.error'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
        <p className="mt-2 text-gray-600">{t('profile.subtitle')}</p>
      </div>

      {/* Profile Info */}
      <Card title={t('profile.accountInfo.title')}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.accountInfo.email')}
            </label>
            <div className="text-gray-900">{profile?.email || user?.email}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.accountInfo.userId')}
            </label>
            <div className="text-gray-900">{profile?.id || user?.id}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.accountInfo.registrationDate')}
            </label>
            <div className="text-gray-900">
              {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US') : '-'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('profile.accountInfo.accountStatus')}
            </label>
            <div className="text-gray-900">
              {profile?.accountStatus === 'ACTIVE' ? t('profile.accountInfo.statusActive') : t('profile.accountInfo.statusBlocked')}
            </div>
          </div>
        </div>
      </Card>

      {/* Change Password */}
      <Card title={t('profile.security.title')}>
        {!showPasswordForm ? (
          <Button 
            variant="secondary" 
            onClick={() => setShowPasswordForm(true)}
          >
            {t('profile.security.changePassword')}
          </Button>
        ) : (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <Input
              label={t('profile.security.newPassword')}
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              placeholder={t('profile.security.passwordPlaceholder')}
              error={passwordErrors.newPassword}
              required
            />
            
            <Input
              label={t('profile.security.confirmPassword')}
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              placeholder={t('profile.security.confirmPasswordPlaceholder')}
              error={passwordErrors.confirmPassword}
              required
            />
            
            <div className="flex space-x-3">
              <Button
                type="submit"
                variant="primary"
                loading={updating}
              >
                {t('profile.security.save')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ newPassword: '', confirmPassword: '' });
                  setPasswordErrors({});
                }}
                disabled={updating}
              >
                {t('profile.security.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Danger Zone */}
      {/* <Card title={t('profile.dangerZone.title')} className="border-red-200">
        <p className="text-sm text-gray-600 mb-4">
          {t('profile.dangerZone.description')}
        </p>
        <Button variant="danger" onClick={() => window.location.href = '/login'}>
          {t('profile.dangerZone.logout')}
        </Button>
      </Card> */}
    </div>
  );
};

export default Profile;
